import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, Quote, Star, Crown, Shield, Award, Gem } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  business: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Local Business' },
  advertiser: { bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Advertiser' },
  supporter: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Supporter' },
};

const TIER_STYLES = {
  platinum: { icon: Gem, bg: 'bg-gradient-to-r from-cyan-400/20 to-indigo-500/20', border: 'border-cyan-300/50', text: 'text-cyan-300', label: 'Platinum Partner', glow: 'shadow-cyan-400/20 shadow-xl' },
  gold: { icon: Crown, bg: 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20', border: 'border-yellow-500/50', text: 'text-yellow-400', label: 'Gold Partner', glow: 'shadow-yellow-500/10 shadow-lg' },
  silver: { icon: Award, bg: 'bg-gradient-to-r from-slate-400/20 to-gray-300/20', border: 'border-slate-400/50', text: 'text-slate-300', label: 'Silver Partner', glow: 'shadow-slate-400/10 shadow-md' },
  bronze: { icon: Shield, bg: 'bg-gradient-to-r from-amber-700/20 to-amber-600/20', border: 'border-amber-700/50', text: 'text-amber-600', label: 'Bronze Partner', glow: '' },
  none: null,
};

const FeaturedPartnerSpotlight = () => {
  const [partners, setPartners] = useState([]);
  const [current, setCurrent] = useState(0);

  const loadPartners = useCallback(async () => {
    try {
      const res = await fetch(`${API}/featured-partners?active_only=true`);
      const data = await res.json();
      setPartners(data);
    } catch (err) {
      console.error('Error loading featured partners:', err);
    }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  useEffect(() => {
    if (partners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % partners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [partners.length]);

  if (partners.length === 0) return null;

  const partner = partners[current];
  const typeStyle = TYPE_COLORS[partner.partner_type] || TYPE_COLORS.business;
  const tierStyle = TIER_STYLES[partner.tier] || null;
  const fallbackImg = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg';

  const prev = () => setCurrent(c => (c - 1 + partners.length) % partners.length);
  const next = () => setCurrent(c => (c + 1) % partners.length);

  // Gold partners get a larger, premium card
  const isGold = partner.tier === 'gold';

  return (
    <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900" data-testid="featured-partner-spotlight">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 bg-[var(--madder)]/10 rounded-full">
            <Star size={14} className="text-[var(--madder)]" />
            <span className="text-[var(--madder)] text-xs font-semibold tracking-wider uppercase">Featured Partner</span>
          </div>
          <h2 className="text-3xl font-bold text-white">
            Partner <span className="text-[var(--madder)]">Spotlight</span>
          </h2>
        </div>

        <div className="relative">
          <div className={`bg-slate-800/50 border rounded-2xl overflow-hidden backdrop-blur-sm ${tierStyle ? tierStyle.border : 'border-slate-700/50'} ${tierStyle ? tierStyle.glow : ''}`}>
            <div className={`grid ${isGold ? 'md:grid-cols-2' : 'md:grid-cols-5'} gap-0`}>
              {/* Logo / Image */}
              <div className={`${isGold ? '' : 'md:col-span-2'} relative bg-slate-800 flex items-center justify-center ${isGold ? 'p-12 min-h-[280px]' : 'p-8 min-h-[220px]'}`}>
                <img
                  src={partner.logo || fallbackImg}
                  alt={partner.name}
                  className={`max-w-full object-contain rounded-lg ${isGold ? 'max-h-52' : 'max-h-40'}`}
                  onError={e => { e.target.src = fallbackImg; }}
                />
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className={`${typeStyle.bg} ${typeStyle.text} text-xs font-medium px-3 py-1 rounded-full`}>
                    {typeStyle.label}
                  </span>
                  {tierStyle && (
                    <span className={`${tierStyle.bg} ${tierStyle.text} text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1`}>
                      {React.createElement(tierStyle.icon, { size: 12 })}
                      {tierStyle.label}
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className={`${isGold ? '' : 'md:col-span-3'} p-8 flex flex-col justify-center`}>
                <Quote size={28} className="text-[var(--madder)]/30 mb-3" />
                <p className={`text-gray-300 leading-relaxed mb-6 italic ${isGold ? 'text-lg' : 'text-base'}`} data-testid="spotlight-testimonial">
                  "{partner.testimonial}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-white font-bold ${isGold ? 'text-xl' : 'text-lg'}`} data-testid="spotlight-name">{partner.name}</h3>
                    <a
                      href={partner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--madder)] text-sm hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Visit website <ExternalLink size={12} />
                    </a>
                  </div>

                  {partners.length > 1 && (
                    <div className="flex items-center gap-2">
                      <button onClick={prev} className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center transition-colors" data-testid="spotlight-prev">
                        <ChevronLeft size={16} />
                      </button>
                      <span className="text-gray-500 text-xs tabular-nums">{current + 1}/{partners.length}</span>
                      <button onClick={next} className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center transition-colors" data-testid="spotlight-next">
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          {partners.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-5">
              {partners.map((p, i) => {
                const dotTier = TIER_STYLES[p.tier];
                const dotColor = dotTier ? dotTier.text.replace('text-', 'bg-') : 'bg-cyan-400';
                return (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === current ? `w-6 ${dotColor}` : 'w-1.5 bg-slate-600 hover:bg-slate-500'
                    }`}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedPartnerSpotlight;
